import React, { useState, useCallback } from "react";
import { Link, Redirect } from "react-router-dom";
import { Button, Card, CardBody, Container, Row, Col } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { logout, refreshAuth } from "store/actions/authActions";
import { toast } from "react-toastify";
import useEmailVerification from "hooks/useEmailVerification";
import firebase from "../../config/firebase";
import Logo from "../../assets/img/custom/logo.png";

function EmailVerificationPending() {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [isVerified, setIsVerified] = useState(false);

  // Called automatically by the hook as soon as polling detects verification
  const handleVerified = useCallback(() => {
    toast.success("Email verified successfully! Redirecting...");
    setIsVerified(true);
    // Refresh Redux auth so App.js re-evaluates the emailVerified guard
    const user = firebase.auth().currentUser;
    if (user) dispatch(refreshAuth(user.uid));
  }, [dispatch]);

  const {
    sendVerificationEmail,
    isSending,
    cooldownRemaining,
  } = useEmailVerification({ onVerified: handleVerified });

  const currentUser = firebase.auth().currentUser;
  const userEmail = currentUser?.email || auth.user?.email || "";

  const handleResendEmail = async () => {
    if (!currentUser) {
      toast.error("Session expired. Please log in again.");
      dispatch(logout());
      return;
    }
    await sendVerificationEmail();
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  if (isVerified) {
    return <Redirect to="/admin/profile" />;
  }

  if (!userEmail) {
    return <Redirect to="/auth/login" />;
  }

  return (
    <>
      <div className="text-center mt-5 mb-4">
        <Link to="/">
          <img src={Logo} alt="logo" style={{ maxWidth: "200px" }} />
        </Link>
      </div>

      <Container className="mt--4 pb-5">
        <Row className="justify-content-center">
          <Col lg="6" md="8" sm="10">
            <Card className="border-0 shadow">
              <CardBody className="px-4 px-lg-5 py-4 py-lg-5">
                <div className="text-center mb-4">
                  <div 
                    className="icon icon-shape bg-gradient-primary rounded-circle text-white mx-auto"
                    style={{ width: "80px", height: "80px" }}
                  >
                    <i className="ni ni-email-83" style={{ fontSize: "2.5rem", lineHeight: "80px" }}></i>
                  </div>
                </div>

                <h2 className="text-center mb-4">Verify Your Email</h2>

                <div className="text-center mb-4">
                  <p className="text-muted mb-1 small">We've sent a verification email to:</p>
                  <p className="font-weight-bold text-dark" style={{ fontSize: "0.95rem", wordBreak: "break-word" }}>
                    {userEmail}
                  </p>
                </div>


                <div className="alert alert-warning mb-4 py-2 px-3">
                  <small className="mb-0 d-flex align-items-center">
                    <i className="ni ni-bulb-61 mr-2"></i>
                    Don't see the email? Check your spam or junk folder
                  </small>
                </div>

                <Button
                  color="secondary"
                  outline
                  size="lg"
                  block
                  className="mb-3"
                  style={{ color: '#000' }}
                  onClick={handleResendEmail}
                  disabled={isSending || cooldownRemaining > 0}
                >
                  {isSending ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Sending...
                    </>
                  ) : cooldownRemaining > 0 ? (
                    `Resend in ${cooldownRemaining}s`
                  ) : (
                    "Resend Verification Email"
                  )}
                </Button>

                <div className="text-center">
                  <Button
                    color="link"
                    size="sm"
                    onClick={handleLogout}
                    className="text-muted"
                  >
                    <i className="ni ni-user-run mr-1"></i>
                    Logout
                  </Button>
                </div>

                <hr className="my-4" />

                <div className="text-center">
                  <small className="text-muted">
                    Having trouble?{" "}
                    <Link to="/auth/login" onClick={handleLogout} className="font-weight-bold">
                      Try logging in again
                    </Link>
                  </small>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default EmailVerificationPending;
